import type { CalendarEvent } from "../models/calendar.model"

/**
 * Column-packing layout for overlapping events — the same algorithm Google
 * Calendar uses to render side-by-side blocks instead of stacking events on
 * top of one another.
 *
 * The flow:
 *   1. Sort events by start time (longest first when ties tie).
 *   2. Walk the list and grow an "overlap cluster" while the next event
 *      starts before the running cluster's max end time.
 *   3. Within each cluster, place each event into the leftmost column where
 *      it doesn't collide with another event already there.
 *   4. Total cluster columns × column index → percent-based left/width so
 *      every event in the cluster shares the time-slot width evenly.
 *
 * The result preserves every event (no collapsing) while keeping each block
 * wide enough to be readable.
 */

export interface EventLayout {
  /** 0-based column index inside the overlap cluster. */
  col: number
  /** Total number of parallel columns in this event's cluster. */
  cols: number
}

export interface LaidOutEvent<E extends CalendarEvent = CalendarEvent> {
  event: E
  layout: EventLayout
}

function timeToMinutes(time: string): number {
  const [h, m] = time.split(":").map(Number)
  return (Number.isFinite(h) ? h : 0) * 60 + (Number.isFinite(m) ? m : 0)
}

export interface LayoutOptions {
  /** Treat every event as lasting at least this many minutes. Pass the
   *  view's minimum card height (in minutes) so short events whose cards
   *  are drawn taller than their duration are packed side by side instead
   *  of being drawn on top of the next event. */
  minDurationMin?: number
}

export function layoutOverlappingEvents<E extends CalendarEvent>(
  events: E[],
  opts: LayoutOptions = {},
): LaidOutEvent<E>[] {
  if (events.length === 0) return []
  const minDuration = opts.minDurationMin ?? 0

  /** Effective end: an event past midnight ends "before" it starts, so run
   *  it to the end of the day, then apply the minimum display duration. */
  const endOf = (ev: E): number => {
    const start = timeToMinutes(ev.startTime)
    const end = timeToMinutes(ev.endTime)
    return Math.max(end < start ? 24 * 60 : end, start + minDuration)
  }

  const sorted = [...events].sort((a, b) => {
    const aStart = timeToMinutes(a.startTime)
    const bStart = timeToMinutes(b.startTime)
    if (aStart !== bStart) return aStart - bStart
    // Tie-break by longer duration first so big blocks anchor the left side.
    return endOf(b) - endOf(a)
  })

  const laidOut: LaidOutEvent<E>[] = []
  let cluster: E[] = []
  let clusterEndMin = -1

  const flush = () => {
    if (cluster.length === 0) return
    // Each column tracks the latest end time of the event last placed there.
    const colEnds: number[] = []
    const clusterAssignments: { ev: E; col: number }[] = []

    for (const ev of cluster) {
      const start = timeToMinutes(ev.startTime)
      // Find the leftmost column where this event fits (start >= colEnd).
      let target = colEnds.findIndex((end) => end <= start)
      if (target === -1) {
        target = colEnds.length
        colEnds.push(endOf(ev))
      } else {
        colEnds[target] = endOf(ev)
      }
      clusterAssignments.push({ ev, col: target })
    }

    const cols = colEnds.length
    for (const { ev, col } of clusterAssignments) {
      laidOut.push({ event: ev, layout: { col, cols } })
    }

    cluster = []
    clusterEndMin = -1
  }

  for (const ev of sorted) {
    const start = timeToMinutes(ev.startTime)
    const end = endOf(ev)
    if (cluster.length === 0 || start < clusterEndMin) {
      cluster.push(ev)
      clusterEndMin = Math.max(clusterEndMin, end)
    } else {
      flush()
      cluster.push(ev)
      clusterEndMin = end
    }
  }
  flush()

  return laidOut
}

/**
 * Convert an `EventLayout` into CSS positioning. Returns percent-based
 * `left` and `width` so the cluster fills the time-slot column with a small
 * inset on either side for breathing room.
 */
export interface LayoutCssOptions {
  /** Outer left/right inset in pixels (applied to the cluster as a whole). */
  outerInsetPx?: number
  /** Gap between columns within a cluster, in pixels. */
  gapPx?: number
}

export function layoutToCss(
  layout: EventLayout,
  opts: LayoutCssOptions = {},
): { left: string; width: string } {
  const outer = opts.outerInsetPx ?? 4
  const gap = opts.gapPx ?? 2
  const colWidthPct = 100 / layout.cols
  return {
    left: `calc(${layout.col * colWidthPct}% + ${outer}px)`,
    width: `calc(${colWidthPct}% - ${outer * 2}px - ${
      layout.cols > 1 ? gap : 0
    }px)`,
  }
}
